import { Injectable } from '@angular/core'
import { ContractStatus, FirestoreStructure, Subscription, subscriptionsPath } from '@parkupp/core'
import { addDoc, arrayUnion, collection, collectionData, deleteDoc, doc, docData, Firestore, getDocs, query, serverTimestamp, updateDoc, where, Query, DocumentData } from '@angular/fire/firestore'
import * as firestore from 'firebase/firestore'
import { AuthenticationService } from './authentication.service'
import { map, Observable, firstValueFrom } from 'rxjs'
import * as moment from 'moment'
import { EnterpriseContextService } from './enterprise-context.service'

@Injectable({
    providedIn: 'root',
})
export class SubscriptionService {
    collection = collection(this.firestore, FirestoreStructure.SUBSCRIPTIONS)
    private formData: any

    constructor(
        private authSvc: AuthenticationService, 
        public firestore: Firestore,
        private enterpriseContext: EnterpriseContextService
    ) {}
    
    /**
     * Apply enterprise filter to a query if enterprise context is active
     */
    private applyEnterpriseFilter(q: Query<DocumentData>): Query<DocumentData> {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        if (enterpriseKey) {
            return query(q, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        return q
    }

    listForLeads(): Observable<Subscription[]> {
        const sixMonthsAgo = firestore.Timestamp.fromDate(moment().subtract(6, 'months').toDate())
        const statusesArray = [ContractStatus.active, ContractStatus.completed, ContractStatus.new]
        const recentSubsQuery = query(this.collection, where('createdAt', '>=', sixMonthsAgo), where('status', 'in', statusesArray))

        return collectionData(recentSubsQuery, { idField: '$key' }).pipe(map((subscriptions: any[]) => subscriptions.map((subscription: any) => new Subscription(subscription))))
    }

    search(filter?: { statuses?: ContractStatus[]; enterpriseKey?: string }): Observable<Subscription[]> {
        let q = query(this.collection)
        if (filter?.statuses) {
            q = query(q, where('status', 'in', filter.statuses))
        }
        // Apply enterprise filter from context or explicit parameter
        const enterpriseKey = filter?.enterpriseKey ?? this.enterpriseContext.currentEnterpriseKey
        if (enterpriseKey) {
            q = query(q, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        return collectionData(q, { idField: '$key' }).pipe(map((subscriptions: any[]) => subscriptions.map((subscription: any) => new Subscription(subscription))))
    }
    
    /**
     * Search subscriptions with explicit enterprise filter (for admin views)
     */
    searchByEnterprise(enterpriseKey: string | null, filter?: { statuses?: ContractStatus[] }): Observable<Subscription[]> {
        let q = query(this.collection)
        if (filter?.statuses) {
            q = query(q, where('status', 'in', filter.statuses))
        }
        if (enterpriseKey) {
            q = query(q, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        return collectionData(q, { idField: '$key' }).pipe(map((subscriptions: any[]) => subscriptions.map((subscription: any) => new Subscription(subscription))))
    }

    getDocRef(subscriptionKey: string) {
        return doc(this.firestore, `${FirestoreStructure.SUBSCRIPTIONS}/${subscriptionKey}`)
    }

    get(subscriptionKey: string): Observable<any> {
        const subscriptionDocRef = doc(this.firestore, `${FirestoreStructure.SUBSCRIPTIONS}/${subscriptionKey}`)
        return docData(subscriptionDocRef, { idField: '$key' })
    }

    async listByClientRef(clientRef: any): Promise<Subscription[]> {
        const clientSubsQuery = query(this.collection, where('clientRef', '==', clientRef))
        const snapshot = await getDocs(clientSubsQuery)
        return snapshot.docs.map((doc) => new Subscription({ ...doc.data(), $key: doc.id }))
    }

    async getPromise(subscriptionKey: string): Promise<Subscription> {
        const subscriptionDocRef = doc(this.firestore, `${FirestoreStructure.SUBSCRIPTIONS}/${subscriptionKey}`)
        const documentData = await firstValueFrom(docData(subscriptionDocRef, { idField: '$key' }))
        return new Subscription(documentData)
    }

    getSubscriptionsByClient(clientRef: any) {
        const clientSubsQuery = query(this.collection, where('clientRef', '==', clientRef))
        return collectionData(clientSubsQuery, { idField: '$key' }).pipe(map((subscriptions: any[]) => subscriptions.map((subscription: any) => new Subscription(subscription))))
    }

    async create(subscription: Subscription) {
        return addDoc(collection(this.firestore, FirestoreStructure.SUBSCRIPTIONS), subscription.parse()).then((docRef) => {
            return { status: 'success', subscriptionReference: docRef.id }
        })
    }

    getSubscription(subscriptionKey: string) {
        return new Promise<any>((resolve, reject) => {
            console.log(subscriptionKey)
            docData(doc(this.firestore, subscriptionsPath(), subscriptionKey)).subscribe((documentData: any) => {
                console.log(documentData)
                let subscription = new Subscription(documentData)
                subscription.$key = subscriptionKey

                // docData(documentData.parkingRef).subscribe((data) => {
                //     subscription.$parking = data as Parking
                //     subscription.$parking.$key = documentData.parkingRef.id
                // })
                // docData(documentData.paymentMethodRef).subscribe((data) => {
                //     subscription.$paymentMethod = data as PaymentMethod;
                //     subscription.$paymentMethod.$key = documentData.paymentMethodRef.id;
                // });
                // docData(documentData.clientRef).subscribe((data) => {
                //     subscription.$client = data as Client;
                //     subscription.$client.$key = documentData.clientRef.id;
                // });
                // if (documentData.occupantRefs) {
                //     subscription.$occupants = [];
                //     documentData.occupantRefs.forEach((occupantsRef: DocumentReference<DocumentData>) => {
                //         docData(occupantsRef).subscribe((data) => {
                //             const occupant = new Occupant(data);
                //             occupant.$key = occupantsRef.id;
                //             subscription.$occupants!.push(occupant);
                //         });
                //
                //     });
                // }
                resolve(subscription)
            })
        })
    }

    sortSubs(subscriptions: Subscription[]) {
        return subscriptions.sort((a, b) => {
            const endDateA = a.endDate.toDate()
            const endDateB = b.endDate.toDate()

            if (endDateA > endDateB) {
                return -1
            }
            if (endDateA < endDateB) {
                return 1
            }
            return 0
        })
    }

    deduplicateSubscriptionsByClientRef(subscriptions: Subscription[]): Subscription[] {
        const deduplicatedSubscriptionsMap = new Map<string, Subscription>()

        subscriptions.forEach((subscription: Subscription) => {
            const clientRefId = subscription.clientRef.id

            if (!deduplicatedSubscriptionsMap.has(clientRefId)) {
                deduplicatedSubscriptionsMap.set(clientRefId, subscription)
            }
        })

        return Array.from(deduplicatedSubscriptionsMap.values())
    }

    /**
     * @param auditEvent Optional event (built via `SubscriptionEventLogService.buildChangeEvent`)
     *   appended to the subscription's `eventLog` in the same write, so the change and the
     *   record of who made it can never come apart.
     */
    async updateSubscription(subscription: Subscription, auditEvent?: Record<string, any> | null): Promise<void> {
        if (!subscription.$key) {
            throw new Error("Subscription does not have a valid '$key'.")
        }

        // Construct the document reference
        const subscriptionDocRef = doc(this.firestore, subscriptionsPath(), subscription.$key)

        // Optionally set the updatedAt timestamp
        subscription.updatedAt = serverTimestamp() as firestore.Timestamp

        const payload = subscription.parse()
        // The log is append-only — writing back our in-memory copy could drop entries added
        // elsewhere since this object was loaded.
        delete payload.eventLog
        if (auditEvent) {
            payload.eventLog = arrayUnion(auditEvent)
        }

        // Perform the update
        await updateDoc(subscriptionDocRef, payload)
            .then(() => {
                console.log(`Subscription with ID ${subscription.$key} updated successfully.`)
            })
            .catch((error) => {
                console.error('Error updating subscription:', error)
                throw new Error('Failed to update subscription.')
            })
    }

    async delete(key: string): Promise<void> {
        // Construct the document reference
        const subscriptionDocRef = doc(this.firestore, `${FirestoreStructure.SUBSCRIPTIONS}/${key}`)

        // Perform the delete operation
        await deleteDoc(subscriptionDocRef)
            .then(() => {
                console.log(`Subscription with ID ${key} deleted successfully.`)
                return
            })
            .catch((error) => {
                console.error('Error deleting subscription:', error)
                throw new Error('Failed to delete subscription.')
            })
    }
}
